import { Injectable } from '@angular/core';

import { Fixture, TemplateCategory } from '@cds/betting-offer';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingApi } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { GridConfig, PrettyUrlsConfig } from '@frontend/sports/common/client-config-data-access';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { EventFactory } from '@frontend/sports/grid/core/feature/factories';
import { Column, GridGrouping, GridModel, GridSorting, Group, SubscriptionTopic } from '@frontend/sports/grid/core/feature/model';
import { GridActions, IGridRootState } from '@frontend/sports/grid/core/feature/store';
import { DeviceService } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isArray } from 'lodash-es';
import { Observable, concatMap, filter, map, of, switchMap, withLatestFrom } from 'rxjs';

import { CalendarService } from '../../../calendar/calendar.service';
import { CompetitionRouteService } from '../../../competition-list/competition-route.service';
import { MarketOfferUrlParam } from '../../../navigation-core/url-helper.service';
import { CompetitionRoute } from '../../../navigation/navigation.models';
import { EventListService } from '../services/event-list.service';
import { SortingStateService } from '../services/sorting-state.service';
import { CompetitionGridActions } from './competition-grid.actions';

@Injectable()
export class CompetitionGridEffects {
    private competitionRoute: boolean;
    private competitionOrCalendarRoute: boolean;

    private get route(): CompetitionRoute {
        return this.eventRoute.params();
    }

    constructor(
        private groupingService: OfferGroupingApi,
        private eventRoute: CompetitionRouteService,
        private eventService: EventListService,
        private eventFactory: EventFactory,
        private routeService: RouterEventsService,
        private urlConfig: PrettyUrlsConfig,
        private calendarService: CalendarService,
        private sortingStore: SortingStateService,
        private actions$: Actions,
        private store: Store<IGridRootState>,
        private gridConfig: GridConfig,
        private deviceService: DeviceService,
    ) {
        this.routeService.currentActivationEnd.subscribe(() => {
            this.competitionRoute =
                this.route.context === this.urlConfig.translations.betting ||
                this.route.context === this.urlConfig.translations.conferences ||
                this.route.context === this.urlConfig.translations.bets;
            this.competitionOrCalendarRoute = this.competitionRoute || this.calendarService.isInterval(this.route.context);
        });
    }

    onSetActiveGroupType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GridActions.setActiveGroupType),
            filter(() => this.competitionRoute),
            concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(map((state) => state.grid[action.payload.id]))))),
            switchMap(([action, grid]) => {
                const params = this.route;
                let topic: SubscriptionTopic | undefined;

                if (action.payload.extended) {
                    params.marketOffer = MarketOfferUrlParam.NonGridable;
                    topic = SubscriptionTopic.NonGridable;
                }

                return this.eventService.getFixtureList(params, grid.sorting).pipe(
                    switchMap((page) =>
                        this.eventFactory.create(page?.fixtures, {
                            marketGrouping: action.payload.extended,
                        }),
                    ),
                    map((events) => {
                        if (events.length === 0) {
                            return [];
                        }

                        return [
                            GridActions.setEvents({ payload: { id: action.payload.id, events, topic } }),
                            GridActions.setActiveGroup({
                                payload: {
                                    id: action.payload.id,
                                    columnId: action.payload.columnId,
                                    groupId: action.payload.groupId,
                                },
                            }),
                        ];
                    }),
                );
            }),
            concatMap((actions) => actions),
        );
    });

    onSortingChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GridActions.changeSorting),
            filter(() => this.competitionOrCalendarRoute),
            concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(map((state) => state.grid[action.payload.id]))))),
            switchMap(([action, grid]) => {
                const grouping = grid.sorting === GridSorting.Competition ? GridGrouping.League : GridGrouping.Date;
                const params = { ...this.route, marketOffer: this.getGridMarketOffer(grid.topic) };

                if (params.isVirtual) {
                    this.sortingStore.setByVirtualCompetitionId(action.payload.sorting, params.league as number);
                } else {
                    this.sortingStore.set('competitions', action.payload.sorting, params.sport);
                }

                return this.eventService.getFixtureList(params, grid.sorting).pipe(
                    switchMap((page) => this.getEvents(page, grid)),
                    map((events) => {
                        if (events.length) {
                            return GridActions.setEvents({ payload: { id: action.payload.id, events, grouping } });
                        }

                        return;
                    }),
                );
            }),
            concatMap((current) => (current ? [current] : [])),
        );
    });

    onAddEvents$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompetitionGridActions.addEvents),
            filter(() => this.competitionOrCalendarRoute),
            concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(map((state) => state.grid[action.payload.id]))))),
            switchMap(([action, grid]) => {
                return this.getEvents(action?.payload, grid).pipe(
                    map((events) =>
                        GridActions.addEvents({
                            payload: {
                                id: grid.id,
                                events,
                            },
                        }),
                    ),
                );
            }),
        );
    });

    onSetMoreCollapse$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GridActions.setMoreCollapse),
            filter(() => this.competitionRoute),
            concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(map((state) => state.grid[action.gridId]))))),
            switchMap(([action, grid]) => {
                if (!this.getExtended(grid, false)) {
                    return of([]);
                }

                const route = this.eventRoute.params();
                const regions = route.region ? [route.region] : [];
                let leagues: number[] = [];
                let virtualCompetitionId;

                if (route.league) {
                    const routeLeagues = isArray(route.league) ? route.league : [route.league];
                    leagues = route.isVirtual ? leagues : routeLeagues;
                    virtualCompetitionId = route.isVirtual ? routeLeagues[0] : virtualCompetitionId;
                }

                return this.groupingService
                    .getFixturesContext({
                        sportId: route.sport,
                        regionIds: regions,
                        competitionIds: leagues,
                        virtualCompetitionIds: virtualCompetitionId,
                        virtualCompetitionGroupIds: route.virtualCompetitionGroup,
                    })
                    .pipe(
                        map((templates) => [
                            GridActions.addGroups({
                                payload: {
                                    id: grid.id,
                                    groups: this.getCategoryList(templates),
                                },
                            }),
                        ]),
                    );
            }),
            concatMap((actions) => actions),
        );
    });

    onInit$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GridActions.init),
            filter(() => this.competitionRoute),
            concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(map((state) => state.grid[action.payload.id]))))),
            switchMap(([action, grid]) => {
                if (
                    !(
                        !!this.route.marketCategory ||
                        grid.columns.some(
                            (column) =>
                                column.groups.some((group) => group.active && group.extended) ||
                                (this.isGridPillsHeaderEnabled() && grid.columns.some((col) => col.more)),
                        )
                    )
                ) {
                    return of([]);
                }

                const route = this.eventRoute.params();
                const regions = route.region ? [route.region] : [];
                let leagues: number[] = [];
                let virtualCompetitionId;

                if (route.league) {
                    const routeLeagues = isArray(route.league) ? route.league : [route.league];
                    leagues = route.isVirtual ? leagues : routeLeagues;
                    virtualCompetitionId = route.isVirtual ? routeLeagues[0] : virtualCompetitionId;
                }

                return this.groupingService
                    .getFixturesContext({
                        sportId: route.sport,
                        regionIds: regions,
                        competitionIds: leagues,
                        virtualCompetitionIds: virtualCompetitionId,
                        virtualCompetitionGroupIds: route.virtualCompetitionGroup,
                    })
                    .pipe(
                        map((templates) => [
                            GridActions.addGroups({
                                payload: {
                                    id: grid.id,
                                    groups: this.getCategoryList(templates),
                                },
                            }),
                        ]),
                    );
            }),
            concatMap((actions) => actions),
        );
    });

    private getEvents(
        source:
            | {
                  fixtures: Fixture[] | undefined;
              }
            | undefined,
        grid: GridModel,
    ): Observable<EventModel[]> {
        return this.eventFactory.create(source?.fixtures, { marketGrouping: grid.topic !== SubscriptionTopic.Grid });
    }

    private getGridMarketOffer(gridType: SubscriptionTopic): string {
        switch (gridType) {
            case SubscriptionTopic.Grid:
                return MarketOfferUrlParam.Gridable;
            case SubscriptionTopic.NonGridable:
                return MarketOfferUrlParam.NonGridable;
            case SubscriptionTopic.Specials:
                return MarketOfferUrlParam.Specials;
            case SubscriptionTopic.Outrights:
                return MarketOfferUrlParam.Outrights;
            default:
                return '';
        }
    }

    private getCategoryList(categories?: TemplateCategory[]): Group[] {
        return (categories || []).map((category) => ({
            id: category.id.toString(),
            name: category.name.value,
            active: false,
            extended: true,
            visible: true,
            fallbackGridGroupIds: [],
            isFallbackGroup: false,
        }));
    }

    private getExtended(grid?: GridModel, extendedActive: boolean = true): boolean {
        if (!grid?.columns) {
            return false;
        }

        const contain = (column: Column) => column.more;
        const active = (column: Column) => column.groups.some((group) => group.active && group.extended);

        return grid.columns.some((column) => (extendedActive ? active(column) : contain(column)));
    }

    private isGridPillsHeaderEnabled(): boolean {
        return this.gridConfig.isMarketPillsEnabled && this.deviceService.isMobilePhone && this.deviceService.currentOrientation !== 'landscape';
    }
}
